/**
 * The way into the Upload Studio from Product sync (Phase 91): a Large
 * cell's "Place on platen" and a Product updates row's "Place". One link for
 * both, so both carry the same router state, `{ from, focus, locationId }`
 * (plus `walk` from Product updates), and both keep the page for Back
 * (`lib/studio-return`).
 *
 * A plain click (no modifier key: a new tab never leaves this page) first
 * calls the page's `leaveForStudio(focus)`. When Back lands and this link is
 * the cell being brought back (`pendingFocus`), it takes that once:
 * `onReturn` brings its row into view and tints it (default: the link
 * itself is scrolled below the action bar), then focus moves here without a
 * second scroll.
 *
 * Shown to anyone who can read (the studio is read-only without org settings
 * manage). Renders nothing outside a router.
 */
import { useEffect, useRef, type MouseEvent } from 'react';
import { Link, useInRouterContext, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { scrollIntoViewBelowActionBar } from '@/lib/scroll-container';
import { cn } from '@/lib/utils';
import { studioPath, type StudioWalk } from '@/features/upload-studio/lib/studio-walk';
import { sameStudioFocus, useStudioReturn, type StudioFocus } from '../lib/studio-return';

interface StudioEntryLinkProps {
  productId: string;
  /** The visible words (already translated). */
  label: string;
  /** The cell this link sits in: Back brings it into view. Null: the page as it was left. */
  focus?: StudioFocus | null;
  /** Product updates: the rows Save and next walks. */
  walk?: StudioWalk | null;
  /** Back landed on this link: bring its row into view (the link takes focus after). */
  onReturn?: (link: HTMLAnchorElement) => void;
  className?: string;
  'data-testid'?: string;
}

export function StudioEntryLink(props: StudioEntryLinkProps) {
  const inRouter = useInRouterContext();
  return inRouter ? <StudioEntryRouterLink {...props} /> : null;
}

/** A click that opens the studio in THIS tab (a modifier opens another tab or window). */
function isSameTabClick(e: MouseEvent<HTMLAnchorElement>): boolean {
  return !e.defaultPrevented && e.button === 0 && !e.metaKey && !e.altKey && !e.ctrlKey && !e.shiftKey;
}

function StudioEntryRouterLink({
  productId,
  label,
  focus = null,
  walk = null,
  onReturn,
  className,
  'data-testid': testId,
}: StudioEntryLinkProps) {
  const location = useLocation();
  const studioReturn = useStudioReturn();
  const ref = useRef<HTMLAnchorElement>(null);
  const state = {
    ...(walk ? { walk } : {}),
    from: `${location.pathname}${location.search}`,
    focus,
    locationId: studioReturn?.locationId ?? null,
  };

  // Back: this link is the cell to bring back. Taken once (StrictMode's
  // second effect run finds it already taken).
  const wanted = focus !== null && sameStudioFocus(studioReturn?.pendingFocus, focus);
  const latest = useRef({ focus, takeFocus: studioReturn?.takeFocus, onReturn });
  latest.current = { focus, takeFocus: studioReturn?.takeFocus, onReturn };
  useEffect(() => {
    if (!wanted) return;
    const { focus: mine, takeFocus, onReturn: bringBack } = latest.current;
    const link = ref.current;
    if (!mine || !takeFocus || !link || !takeFocus(mine)) return;
    if (bringBack) bringBack(link);
    else scrollIntoViewBelowActionBar(link);
    link.focus({ preventScroll: true });
  }, [wanted]);

  return (
    // D10: the same outline h-8 button for every way into the studio.
    <Button asChild variant="outline" size="sm" className={cn('h-8 shrink-0 px-3 text-xs', className)}>
      <Link
        ref={ref}
        to={studioPath(productId)}
        state={state}
        data-testid={testId}
        onClick={(e) => {
          if (isSameTabClick(e)) studioReturn?.leaveForStudio(focus);
        }}
      >
        {label}
      </Link>
    </Button>
  );
}
